// Minimal UI internationalization — a flat "key -> template" table per
// language, ported from i18n.py. Missing keys fall back to English.
// Templates use {name} placeholders.

export const DEFAULT_LANG = 'en';
export type Lang = 'ko' | 'en';
export type LangPref = 'system' | Lang;

// (code, native name) for the selectable languages, in Settings order.
export const SELECTABLE_LANGS: Array<[Lang, string]> = [
  ['ko', '한국어'],
  ['en', 'English'],
];

/**
 * Browser UI language detection.
 *
 * `navigator.languages` is ordered by the user's preference, so the first
 * entry we actually support wins. Scanning for any Korean tag anywhere in the
 * list would be wrong: a browser set to ["en-US", "ko"] prefers English, and
 * "ko" is only its fallback.
 */
export function detectSystemLang(): Lang {
  try {
    const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const loc of locales) {
      const tag = (loc || '').toLowerCase();
      if (tag.startsWith('ko')) return 'ko';
      if (tag.startsWith('en')) return 'en';
    }
  } catch {
    // ignore
  }
  return DEFAULT_LANG; // no supported language in the list
}

type Table = Record<string, string>;

const TRANSLATIONS: Record<Lang, Table> = {
  en: {
    'action.retry': 'Retry',
    'action.clear': 'Clear',
    'action.close': 'Close',
    'dialog.delete_unit_title': 'Delete Unit',
    'dialog.delete_unit_body':
      "Delete unit '{name}'?\nThis will permanently delete {count} sentences.",
    'dialog.delete': 'Delete',
    'dialog.cancel': 'Cancel',
    'error.title': 'Error',
    'error.file_read_failed': "Couldn't read the file (check its encoding).",
    'nav.add_sentences_button': '➕ Add Sentences',
    'nav.prev': '◀ Previous',
    'nav.next': 'Next ▶',
    'nav.back': 'Back',
    'unit.new_label': '➕ Create new unit…',
    'unit.count_suffix': 'items',
    'unit_list.empty_title': 'No sentences saved yet',
    'unit_list.empty_body':
      'Use the [➕ Add Sentences] button above to type sentences or import a text file.',
    'unit_row.sentence_count': '{total} sentences',
    'screen.add_sentences.title': 'Add Sentences',
    'screen.add_sentences.subtitle': 'Type or import a file — every line becomes a study item',
    'add.unit_select_label': 'Select Unit',
    'add.part_name_label': 'Part Name',
    'add.unit_name_label': 'Unit Name',
    'add.voice_label': 'Voice',
    'add.voice_hint': '※ Uses your browser’s built-in English speech engine.',
    'add.persona_checkbox': 'Auto-assign voices per character (persona)',
    'add.persona_hint':
      '※ Lines like "MONICA: ..." won’t have the name read aloud — each character gets a different voice.',
    'add.text_input_label': 'Enter sentences — one per line:  English | Meaning',
    'add.text_input_example1': 'e.g.  Nice to meet you. | Meaning here.   (meaning is optional)',
    'add.text_input_example2': 'e.g.  MONICA: There’s nothing to tell!   (when using persona)',
    'add.import_file_button': '📄 Import File (.txt/.csv/.pdf)',
    'add.import_pdf_opening': '📄 Opening PDF…',
    'add.import_pdf_progress': '📄 Reading page {page} / {pages}…',
    'add.save_button': '💾 Save Sentences',
    'dialog.no_sentences_title': 'No Sentences',
    'dialog.no_sentences_body': 'Type or import the sentences you want to add.',
    'dialog.success_title': 'Done',
    'dialog.success_body': 'Saved {ok} sentences.',
    'screen.scenario_menu.title': 'Choose Activity',
    'scenario.progress_label': 'Progress',
    'mode.card_play.title': 'Flashcards',
    'mode.card_play.desc': 'Flip through cards to listen and learn expressions',
    'mode.dictation.title': 'Dictation',
    'mode.dictation.desc': 'Listen and practice spelling what you hear',
    'mode.expression_list.title': 'Expression List',
    'mode.expression_list.desc': 'See every expression in this unit at a glance',
    'card.skip_checkbox': 'Skip',
    'card.tap_show_english': '( Tap to reveal English )',
    'card.tap_hint_meaning': 'Tap to reveal the meaning',
    'card.tap_show_meaning': '( Tap to reveal meaning )',
    'card.tap_hint_english': 'Tap to reveal the English',
    'card.speaking': '🔊 Speaking…',
    'card.play_button': 'Play',
    'card.stop_button': 'Stop',
    'dictation.meaning_label': 'Meaning',
    'dictation.show_meaning': 'Show Meaning',
    'dictation.hide_meaning': 'Hide Meaning',
    'dictation.replay_button': '▶  Replay',
    'dictation.input_label': 'Type the English sentence you heard',
    'dictation.correct': 'Correct! 🎉',
    'dictation.incorrect': 'Not quite — try again',
    'dictation.answer_prefix': 'Answer: {answer}',
    'dictation.check_button': 'Check Answer',
    'dictation.speak_button': '🎤  Speak the sentence',
    'dictation.listening': '● Listening… tap to stop',
    'dictation.mic_hint':
      '※ Speech recognition sends audio to your browser’s speech service — the only part of this app that uses the network.',
    'dictation.mic_denied':
      'Microphone access is blocked. Allow it from the icon in your address bar, then try again.',
    'dictation.mic_no_speech': 'Didn’t catch that — try again.',
    'dictation.mic_no_device': 'No microphone was found.',
    'dictation.mic_network': 'Speech recognition needs a network connection.',
    'dictation.mic_failed': 'Speech recognition failed. You can still type your answer.',
    'screen.settings.title': 'Settings',
    'settings.display_options': 'Display Options',
    'settings.show_english': 'Show English',
    'settings.show_meaning': 'Show Meaning',
    'settings.auto_advance': 'Auto-advance',
    'settings.playback_speed': 'Playback Speed',
    'settings.repeat_count': 'Repeat Count',
    'settings.repeat_option': '{n}x',
    'settings.repeat_infinite': '∞',
    'settings.language_label': 'Language',
    'settings.language_system': 'Follow System Language',
    'voice.female': 'female',
    'voice.male': 'male',
    'voice.country_us': 'US',
    'voice.default': 'Default US voice',
    // --- web-only additions ---
    'data.section_label': 'Data',
    'data.export_button': '⬇ Export JSON',
    'data.import_button': '⬆ Import JSON',
    'data.import_confirm_title': 'Import Data',
    'data.import_confirm_body': 'Replace the current {current} unit(s) with {incoming} imported unit(s)?',
    'data.import_failed': 'That file is not a valid Speak2Remember export.',
    'data.import_done': 'Imported {count} unit(s).',
    'tts.unsupported_title': 'Speech Not Available',
    'tts.unsupported_body':
      'This browser does not support speech synthesis. Text study still works, but sentences cannot be spoken. Try Chrome, Edge, or Safari.',
    'pdf.err_password': 'That PDF is password-protected. Remove the password and try again.',
    'pdf.err_corrupt': "Couldn't read that PDF — it may be damaged.",
    'pdf.err_no_text':
      'No text found in that PDF. Scanned pages are images, so they need OCR before they can be imported.',
    'mp3.export_button': '🎧 Export MP3',
    'mp3.title': 'Export MP3',
    'mp3.intro_body':
      'Reads {count} sentence(s) aloud and saves them as an MP3 — about {minutes} minute(s), recorded in real time.',
    'mp3.intro_howto':
      '※ The browser cannot record speech on its own, so it will ask what to share: choose Entire Screen and tick "Also share system audio". Without that the file comes out silent.',
    'mp3.start_button': 'Start',
    'mp3.phase_speaking': 'Recording — sentence {done} of {total}',
    'mp3.phase_encoding': 'Encoding MP3… {pct}%',
    'mp3.keep_quiet':
      '※ System audio records everything you hear — keep other sound off until this finishes.',
    'mp3.done_body': 'Saved {name}.',
    'mp3.err_unsupported':
      'This browser cannot record audio. MP3 export needs desktop Chrome or Edge.',
    'mp3.err_denied': 'Screen sharing was cancelled, so there was nothing to record.',
    'mp3.err_no_audio':
      'That share had no audio. Try again and tick "Also share system audio" in the picker.',
    'mp3.err_silent':
      'The recording came out silent. Check that system audio was shared and that your speaker volume is up, then try again.',
    'mp3.err_aborted': 'Export stopped.',
    'mp3.err_failed': 'MP3 export failed.',
  },
  ko: {
    'action.retry': '다시 시도',
    'action.clear': '지우기',
    'action.close': '닫기',
    'dialog.delete_unit_title': '단원 삭제',
    'dialog.delete_unit_body': "'{name}' 단원을 삭제할까요?\n문장 {count}개가 삭제되며 되돌릴 수 없습니다.",
    'dialog.delete': '삭제',
    'dialog.cancel': '취소',
    'error.title': '오류',
    'error.file_read_failed': '파일을 읽을 수 없습니다 (인코딩 확인).',
    'nav.add_sentences_button': '➕ 문장 추가',
    'nav.prev': '◀ 이전',
    'nav.next': '다음 ▶',
    'nav.back': '뒤로',
    'unit.new_label': '➕ 새 단원 만들기…',
    'unit.count_suffix': '개',
    'unit_list.empty_title': '아직 저장된 문장이 없습니다',
    'unit_list.empty_body': '위의 [➕ 문장 추가] 버튼으로 문장을 입력하거나 텍스트 파일을 불러오세요.',
    'unit_row.sentence_count': '문장 {total}개',
    'screen.add_sentences.title': '문장 추가',
    'screen.add_sentences.subtitle': '타이핑하거나 파일을 불러오면 한 줄이 학습 아이템이 됩니다',
    'add.unit_select_label': '단원 선택',
    'add.part_name_label': 'Part 이름',
    'add.unit_name_label': 'Unit 이름',
    'add.voice_label': '목소리',
    'add.voice_hint': '※ 브라우저에 내장된 영어 음성 엔진을 사용합니다.',
    'add.persona_checkbox': '등장인물별 목소리 자동 배정 (persona)',
    'add.persona_hint':
      '※ "MONICA: ..." 처럼 이름으로 시작하는 줄은 이름은 읽지 않고, 인물마다 다른 목소리를 배정합니다.',
    'add.text_input_label': '문장 입력 — 한 줄에 하나:  English | 한국어 뜻',
    'add.text_input_example1': '예)  Nice to meet you. | 만나서 반갑습니다.   (뜻은 생략 가능)',
    'add.text_input_example2': '예)  MONICA: There’s nothing to tell!   (persona 사용 시)',
    'add.import_file_button': '📄 파일 불러오기 (.txt/.csv/.pdf)',
    'add.import_pdf_opening': '📄 PDF 여는 중…',
    'add.import_pdf_progress': '📄 {page} / {pages} 쪽 읽는 중…',
    'add.save_button': '💾 문장 저장',
    'dialog.no_sentences_title': '문장 없음',
    'dialog.no_sentences_body': '추가할 문장을 입력하거나 불러오세요.',
    'dialog.success_title': '완료',
    'dialog.success_body': '{ok}개 문장을 저장했습니다.',
    'screen.scenario_menu.title': '학습 선택',
    'scenario.progress_label': '달성률',
    'mode.card_play.title': '표현학습',
    'mode.card_play.desc': '카드를 넘기며 표현을 듣고 학습합니다',
    'mode.dictation.title': '받아쓰기',
    'mode.dictation.desc': '발음을 듣고 정확한 스펠링을 학습합니다',
    'mode.expression_list.title': '표현 목록',
    'mode.expression_list.desc': '이 단원의 모든 표현을 한 눈에 봅니다',
    'card.skip_checkbox': '건너뛰기',
    'card.tap_show_english': '( 화면을 눌러 영어 보기 )',
    'card.tap_hint_meaning': '화면을 누르면 뜻이 표시됩니다',
    'card.tap_show_meaning': '( 화면을 눌러 뜻 보기 )',
    'card.tap_hint_english': '화면을 누르면 영어가 표시됩니다',
    'card.speaking': '🔊 재생 중…',
    'card.play_button': '재생',
    'card.stop_button': '정지',
    'dictation.meaning_label': '뜻',
    'dictation.show_meaning': '뜻 보기',
    'dictation.hide_meaning': '뜻 숨기기',
    'dictation.replay_button': '▶  다시 듣기',
    'dictation.input_label': '들은 영어 문장을 입력하세요',
    'dictation.correct': '정답입니다! 🎉',
    'dictation.incorrect': '다시 확인해 보세요',
    'dictation.answer_prefix': '정답: {answer}',
    'dictation.check_button': '정답 확인',
    'dictation.speak_button': '🎤  문장 말하기',
    'dictation.listening': '● 듣는 중… 탭하면 중지',
    'dictation.mic_hint':
      '※ 음성 인식은 브라우저의 음성 서비스로 오디오를 전송합니다 — 이 앱에서 네트워크를 사용하는 유일한 기능입니다.',
    'dictation.mic_denied': '마이크 사용이 차단되었습니다. 주소창의 아이콘에서 허용한 뒤 다시 시도하세요.',
    'dictation.mic_no_speech': '소리를 인식하지 못했습니다 — 다시 시도하세요.',
    'dictation.mic_no_device': '마이크를 찾을 수 없습니다.',
    'dictation.mic_network': '음성 인식에는 네트워크 연결이 필요합니다.',
    'dictation.mic_failed': '음성 인식에 실패했습니다. 직접 입력할 수 있습니다.',
    'screen.settings.title': '설정',
    'settings.display_options': '모양 옵션',
    'settings.show_english': '영어 표시',
    'settings.show_meaning': '뜻(한국어) 표시',
    'settings.auto_advance': '자동 넘김',
    'settings.playback_speed': '재생 속도',
    'settings.repeat_count': '반복 횟수',
    'settings.repeat_option': '{n}회',
    'settings.repeat_infinite': '∞',
    'settings.language_label': '언어',
    'settings.language_system': '시스템 언어 사용',
    'voice.female': '여성',
    'voice.male': '남성',
    'voice.country_us': '미국',
    'voice.default': '기본 미국 음성',
    // --- web-only additions ---
    'data.section_label': '데이터',
    'data.export_button': '⬇ JSON 내보내기',
    'data.import_button': '⬆ JSON 가져오기',
    'data.import_confirm_title': '데이터 가져오기',
    'data.import_confirm_body': '현재 {current}개 단원을 가져온 {incoming}개 단원으로 교체할까요?',
    'data.import_failed': 'Speak2Remember 내보내기 파일이 아닙니다.',
    'data.import_done': '{count}개 단원을 가져왔습니다.',
    'tts.unsupported_title': '음성 사용 불가',
    'tts.unsupported_body':
      '이 브라우저는 음성 합성을 지원하지 않습니다. 텍스트 학습은 가능하지만 문장을 읽어주지 못합니다. Chrome, Edge, Safari를 사용해 보세요.',
    'pdf.err_password': '암호가 걸린 PDF입니다. 암호를 해제한 뒤 다시 시도하세요.',
    'pdf.err_corrupt': 'PDF를 읽을 수 없습니다 — 파일이 손상되었을 수 있습니다.',
    'pdf.err_no_text':
      'PDF에서 텍스트를 찾지 못했습니다. 스캔된 페이지는 이미지이므로 OCR을 거쳐야 불러올 수 있습니다.',
    'mp3.export_button': '🎧 MP3 내보내기',
    'mp3.title': 'MP3 내보내기',
    'mp3.intro_body':
      '문장 {count}개를 소리 내어 읽고 MP3로 저장합니다 — 실시간 녹음이라 약 {minutes}분 걸립니다.',
    'mp3.intro_howto':
      '※ 브라우저는 음성을 직접 녹음할 수 없어 화면 공유를 요청합니다. [전체 화면]을 고르고 "시스템 오디오도 공유"를 체크하세요. 체크하지 않으면 무음 파일이 됩니다.',
    'mp3.start_button': '시작',
    'mp3.phase_speaking': '녹음 중 — {total}개 중 {done}번째',
    'mp3.phase_encoding': 'MP3 변환 중… {pct}%',
    'mp3.keep_quiet': '※ 시스템 오디오는 들리는 소리를 모두 녹음합니다. 끝날 때까지 다른 소리는 꺼 두세요.',
    'mp3.done_body': '{name} 파일을 저장했습니다.',
    'mp3.err_unsupported':
      '이 브라우저는 오디오를 녹음할 수 없습니다. MP3 내보내기는 데스크톱 Chrome 또는 Edge가 필요합니다.',
    'mp3.err_denied': '화면 공유가 취소되어 녹음할 것이 없었습니다.',
    'mp3.err_no_audio': '공유에 오디오가 없습니다. 다시 시도할 때 "시스템 오디오도 공유"를 체크하세요.',
    'mp3.err_silent':
      '녹음이 무음으로 저장되었습니다. 시스템 오디오 공유와 스피커 음량을 확인한 뒤 다시 시도하세요.',
    'mp3.err_aborted': '내보내기를 중단했습니다.',
    'mp3.err_failed': 'MP3 내보내기에 실패했습니다.',
  },
};

export function translate(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  const table = TRANSLATIONS[lang] ?? TRANSLATIONS[DEFAULT_LANG];
  let template = table[key] ?? TRANSLATIONS[DEFAULT_LANG][key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      template = template.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return template;
}
